import React from 'react'
import { PrepaidShareType } from 'src/lib/types/rtoSuite'
import { useLocale } from 'src/lib/hooks/useLocal'
import { Trash, Plus, PlusCircle, CurrencyInr } from '@phosphor-icons/react'
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Icon,
  CheckboxV2,
  IconButton
} from '@shopflo/ui'
import { useFormContext, FieldArrayWithId, useWatch } from 'react-hook-form'
import { SplitCodFormValues } from 'src/lib/schemas/splitCodSchema'

interface SplitCodDialogueContentPropTypes {
  fields: FieldArrayWithId<SplitCodFormValues, 'slabs', 'id'>[]
  remove: (index: number) => void
  append: () => void
}

const SplitCodDialogueContentProp: React.FC<SplitCodDialogueContentPropTypes> = ({ fields, remove, append }) => {
  const { t } = useLocale()
  const { control, setValue, trigger, clearErrors } = useFormContext<SplitCodFormValues>()
  const slabs = useWatch({ control, name: 'slabs' }) || []

  return (
    <div className='flex flex-col gap-1.5'>
      {fields.map((field, index) => (
        <div key={field.id} className='flex flex-col  bg-white gap-1.5 pb-4 mb-2 border-b-1 border-zinc-500/15'>
          <div className='flex justify-between items-center mb-1'>
            <div className='flex items-center gap-1.5'>
              <div className='rounded-full w-5 h-5 flex items-center justify-center bg-zinc-100 text-text-xs-bold text-zinc-500'>
                {index + 1}
              </div>
              <h3 className='text-text-sm-semibold text-zinc-700'>Slab</h3>
            </div>
            {fields.length > 1 && (
              <IconButton
                variant='ghost'
                size='sm'
                onClick={() => {
                  remove(index)
                  clearErrors('slabs')
                }}
                type='button'
                IconComponent={Trash}
              />
            )}
          </div>

          <div className='flex gap-3 mb-2'>
            <div className='flex-1'>
              <FormField
                control={control}
                name={`slabs.${index}.minOrderValue`}
                render={({ field }) => (
                  <FormItem className='flex flex-col gap-1.5 space-y-0'>
                    <FormLabel>{t('split_cod.min_order_value')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value === 0 ? '' : field.value}
                        onChange={e => {
                          const val = e.target.value === '' ? '' : Number(e.target.value)
                          field.onChange(val === '' ? 0 : val)
                        }}
                        dimension='md'
                        startAdornment={<Icon IconComponent={CurrencyInr} size='xxs' />}
                        type='number'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className='flex-1'>
              <FormField
                control={control}
                name={`slabs.${index}.maxOrderValue`}
                render={({ field }) => (
                  <FormItem className='flex flex-col gap-1.5 space-y-0'>
                    <FormLabel>{t('split_cod.max_order_value')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        onChange={e => {
                          const val = e.target.value
                          field.onChange(val === '' ? null : Number(val))
                        }}
                        dimension='md'
                        startAdornment={<Icon IconComponent={CurrencyInr} size='xxs' />}
                        type='number'
                        placeholder={t('split_cod.no_limit')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className='flex flex-col gap-2 mb-2'>
            <FormLabel className='text-text-sm-regular'>{t('split_cod.upfront_payment_amount')}</FormLabel>
            <div className='flex gap-3 items-start'>
              <div className='flex-1'>
                <FormField
                  control={control}
                  name={`slabs.${index}.prepaidShareType`}
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={val => {
                          field.onChange(val)
                          if (val === PrepaidShareType.Percentage) {
                            const currentPrepaidShare = slabs?.[index]?.prepaidShare || 0
                            if (currentPrepaidShare > 100) {
                              setValue(`slabs.${index}.prepaidShare`, 100)
                            }
                          }
                          trigger(`slabs.${index}.prepaidShare`)
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className='rounded-lg h-9'>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={PrepaidShareType.Percentage}>Percentage</SelectItem>
                          <SelectItem value={PrepaidShareType.Flat}>Flat Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
              <div className='flex-1'>
                <FormField
                  control={control}
                  name={`slabs.${index}.prepaidShare`}
                  render={({ field: inputField }) => {
                    const type = slabs[index]?.prepaidShareType ?? field.prepaidShareType
                    return (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...inputField}
                            type='number'
                            value={inputField.value === 0 ? '' : inputField.value}
                            onChange={e => {
                              const val = e.target.value === '' ? 0 : parseInt(e.target.value)
                              inputField.onChange(val)
                            }}
                            endAdornment={
                              type === PrepaidShareType.Percentage ? (
                                <span className='text-gray-500 text-sm pr-2'>%</span>
                              ) : (
                                <></>
                              )
                            }
                            startAdornment={
                              type === PrepaidShareType.Flat ? <Icon IconComponent={CurrencyInr} size='xxs' /> : <></>
                            }
                            dimension='md'
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
              </div>
            </div>
          </div>

          <FormField
            control={control}
            name={`slabs.${index}.isCODChargesExcluded`}
            render={({ field }) => (
              <FormItem className='flex flex-row items-center space-x-2 space-y-0 mt-1'>
                <FormControl>
                  <CheckboxV2 checked={field.value} onCheckedChange={field.onChange} size='sm' />
                </FormControl>
                <FormLabel className='text-text-sm-regular cursor-pointer'>
                  {t('split_cod.exclude_cod_charges_from_split_calculation')}
                </FormLabel>
              </FormItem>
            )}
          />
        </div>
      ))}

      <Button variant='ghost' size='sm' onClick={append} type='button' leftIcon={PlusCircle} className='w-fit'>
        <span>{t('split_cod.add_new_slab')}</span>
      </Button>
    </div>
  )
}

export default SplitCodDialogueContentProp
